import { computed, inject, Injectable } from '@angular/core';
import { FeatureToggleType } from '@lib/feature-toggle/models';
import { FeatureToggleService } from './feature-toggle.service';

/**
 * Whether the Pathfinder V2 stack is switched on: the offline cache, offline-capable search, live
 * reference-data updates, and the outbound command queue.
 *
 * <h3>One gate for the whole stack</h3>
 *
 * The parts are not independently usable. Offline search over a cache nothing fills reports "no results" for a
 * tenant's entire master file; an outbound queue with no cache behind it has nothing to write to. Any
 * combination other than all-on or all-off is a broken configuration, so there is nothing to gain from being
 * able to express one — and three separate predicates is exactly how the environment lists drifted apart
 * before this existed (the queue drain covered Development and QA while the UI over it was still
 * localhost-only, so QA delivered commands under a permanent "Synced" with no way to see a dead letter).
 *
 * <h3>THE TOGGLE IS THE ONLY INPUT — in every environment</h3>
 *
 * There is deliberately no environment check here any more. This gate used to read `toggle OR non-production`,
 * because the `Pathfinder V2` feature row ships DISABLED and making the toggle authoritative before the row
 * existed would have switched the stack off on every developer and QA machine. The row is deployed now, so
 * that scaffolding is gone (offline plan, Step 5) and the consequence is the point: **V2 is off everywhere
 * until someone enables it for a tenant**, and it can be turned off again for a tenant in any environment —
 * including a developer's own, which the environment half made impossible.
 *
 * Turning it on takes BOTH flags: `Feature.IsEnabled` (the global kill switch) and that tenant's
 * `TenantFeature.IsEnabled`. `spGetEnabledFeaturesByTenantId` combines them, so what arrives here is already
 * the effective value.
 *
 * <h3>Why it is a SIGNAL and not a boolean</h3>
 *
 * Feature toggles arrive by HTTP after login. Anything that reads this at construction time reads "not loaded
 * yet", and a plain boolean would freeze that answer for the session — so the whole stack would stay dead and
 * nothing would report why. Consumers must read it where the decision is made, and lazily-built machinery must
 * be built from an effect that waits for it (see `PathfinderQueueProcessorService`).
 */
@Injectable({ providedIn: 'root' })
export class PathfinderV2Gate {
    private readonly featureToggleService = inject(FeatureToggleService);

    /** True where the V2 stack may run. Read it at the point of decision — never cache it into a boolean. */
    public readonly isEnabled = computed(() =>
        this.featureToggleService
            .tenantFeaturesComputedSignal()
            .some((feature) => feature.id === FeatureToggleType.PathfinderV2 && feature.isEnabled),
    );
}
